import type { ReactNode } from 'react';
import { cx } from '@parrot/utils';
import { Notice } from '@parrot/ui';
import { LoginBackdrop } from '../LoginBackdrop/LoginBackdrop';
import styles from './AuthLayout.module.css';

const LOGO_URL =
  'https://cdn-fastly.parrotanalytics.com/images/production_planner/placeholders/logomark.svg';
const SUPPORT_EMAIL = 'support@parrotanalytics.com';
const PRIVACY_URL = 'https://www.parrotanalytics.com/privacy-policy/';
const TERMS_URL = 'https://www.parrotanalytics.com/terms-of-service/';

type Props = {
  /** Shown under the logo. */
  productName: string;
  title: string;
  subtitle: string;
  /** Success banner above the body. */
  notice?: ReactNode;
  /** Failure banner above the body. */
  failure?: ReactNode;
  /** Omit to inherit the document's `data-theme`. Only pass to force a subtree. */
  theme?: 'dark' | 'light';
  className?: string;
  children: ReactNode;
};

/**
 * The chrome every auth screen shares — backdrop, card, logo, headings, banners, footer.
 * Internal: the pages are the public surface, so this can change shape without a release
 * note. It renders no form of its own; each page supplies its own body.
 */
export const AuthLayout = ({
  productName,
  title,
  subtitle,
  notice,
  failure,
  theme,
  className,
  children,
}: Props) => (
  <div className={cx(styles.base, className)} data-theme={theme}>
    <LoginBackdrop />

    <main className={styles.card}>
      <div className={styles.cardInner}>
        <div className={styles.logo}>
          <img src={LOGO_URL} alt={''} className={styles.logoImage} />
          <span className={styles.logoText}>{productName}</span>
        </div>

        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>

        {notice !== undefined && (
          <Notice tone={'success'} className={styles.banner}>
            {notice}
          </Notice>
        )}
        {failure !== undefined && (
          <Notice tone={'danger'} className={styles.banner}>
            {failure}
          </Notice>
        )}

        {children}
      </div>
    </main>

    <footer className={styles.footer}>
      <p className={styles.contact}>
        {'Don’t have access? Contact '}
        <a href={`mailto:${SUPPORT_EMAIL}`} className={styles.contactLink}>
          {SUPPORT_EMAIL}
        </a>
      </p>
      <p className={styles.legal}>
        <a
          href={PRIVACY_URL}
          target={'_blank'}
          rel={'noopener noreferrer'}
          className={styles.legalLink}
        >
          {'Privacy Policy'}
        </a>
        <span className={styles.legalDot} />
        <a
          href={TERMS_URL}
          target={'_blank'}
          rel={'noopener noreferrer'}
          className={styles.legalLink}
        >
          {'Terms of Service'}
        </a>
      </p>
      <p className={styles.copyright}>
        {`© ${new Date().getFullYear()} Parrot Analytics. All rights reserved.`}
      </p>
    </footer>
  </div>
);
